import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  RefreshControl,
  Modal,
  ScrollView,
  Linking,
  ActivityIndicator,
  useWindowDimensions,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useAuth } from '../context/AuthContext';
import { homeworkAPI } from '../services/api';
import { HomeworkItem, RootStackParamList } from '../types';
import { mapHomework } from '../utils/mappers';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { Colors, Spacing, FontSize, BorderRadius } from '../theme/colors';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { WebPageShell } from '../components/common/WebPageShell';
import { useTheme, ThemeColors } from '../context/ThemeContext';
import { HomeworkHandInSection } from '../components/homework/HomeworkHandInSection';

type Nav = NativeStackNavigationProp<RootStackParamList>;

const FILTERS = ['All', 'Pending', 'Submitted', 'Overdue'] as const;
type Filter = (typeof FILTERS)[number];

const STATUS_COLOR: Record<string, string> = {
  pending: Colors.warning,
  submitted: Colors.success,
  overdue: Colors.error,
};

const AttachmentPreview = ({ url, hwTitle }: { url: string, hwTitle: string }) => {
  const { colors } = useTheme();
  const [attachment, setAttachment] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    homeworkAPI.getAttachment(url)
      .then(res => {
        if (active && res.data) {
           setAttachment(res.data);
        }
      })
      .catch(err => {
         console.error('Attachment fetch error:', err);
         if (active) setError(true);
      })
      .finally(() => {
         if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [url]);

  if (loading) return <ActivityIndicator size="small" color={colors.primary} style={{ marginVertical: 12 }} />;
  if (error || !attachment) return null;

  const handleDownload = () => {
    if (typeof (globalThis as any).window !== 'undefined') {
        const link = (globalThis as any).document.createElement('a');
        link.href = `data:${attachment.contentType};base64,${attachment.imageBase64}`;
        link.download = attachment.fileName || hwTitle;
        (globalThis as any).document.body.appendChild(link);
        link.click();
        (globalThis as any).document.body.removeChild(link);
    }
  };

  const isImage = attachment.contentType?.startsWith('image/');
  const isPdf = attachment.contentType === 'application/pdf';

  return (
     <View style={{ marginTop: 12 }}>
       {isImage && (
         <img
           src={`data:${attachment.contentType};base64,${attachment.imageBase64}`}
           alt="preview"
           style={{ width: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 8, marginBottom: 12, backgroundColor: colors.divider }}
         />
       )}
       {isPdf && (
         <iframe
           src={`data:${attachment.contentType};base64,${attachment.imageBase64}`}
           style={{ width: '100%', height: 300, border: 'none', borderRadius: 8, marginBottom: 12, backgroundColor: colors.divider }}
         />
       )}
       <TouchableOpacity
         style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: colors.surface, padding: 12, borderRadius: 12, borderWidth: 1, borderColor: colors.border }}
         onPress={handleDownload}
       >
           <View style={{ width: 36, height: 36, borderRadius: 8, backgroundColor: colors.primaryLight, justifyContent: 'center', alignItems: 'center', marginRight: 12 }}>
             <FeatherIcon name="file" size={16} color={colors.primary} />
           </View>
           <View style={{ flex: 1 }}>
             <Text style={{ fontSize: 14, fontWeight: '600', color: colors.textPrimary, marginBottom: 2 }} numberOfLines={1}>
               {attachment.fileName || 'Attachment'}
             </Text>
             <Text style={{ fontSize: 12, color: colors.textSecondary }}>Tap to download</Text>
           </View>
           <View style={{ padding: 4 }}>
             <FeatherIcon name="download" size={18} color={colors.primary} />
           </View>
       </TouchableOpacity>
     </View>
  );
};

export function HomeworkScreen({ isNested }: { isNested?: boolean }) {
  const navigation = useNavigation<Nav>();
  const { selectedStudent } = useAuth();
  const { colors } = useTheme();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [homework, setHomework] = useState<HomeworkItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedHomework, setSelectedHomework] = useState<HomeworkItem | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const loadHomework = useCallback(async () => {
    if (!selectedStudent) {
      setIsLoading(false);
      setRefreshing(false);
      return;
    }
    try {
      const now = new Date();
      const start = new Date(now);
      start.setDate(now.getDate() - 30);
      const end = new Date(now);
      end.setDate(now.getDate() + 30);

      const formatDate = (d: Date) => {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      };

      // Homework from BOTH modules: the legacy Home Work screen, and any
      // homework-type assessment set through the merged New Assessment form.
      // The split is decided server-side (isHomework), so this tab and the
      // Assessments tab can never both claim the same row.
      const feed = await homeworkAPI.getHomeworkFeed(
        selectedStudent.classId,
        formatDate(start),
        formatDate(end),
        selectedStudent.enrollmentId
      );
      const mappedData = feed.homework.map(mapHomework);
      mappedData.sort((a: HomeworkItem, b: HomeworkItem) => {
        const dateA = a.assignedDate ? new Date(a.assignedDate).getTime() : new Date(a.dueDate).getTime();
        const dateB = b.assignedDate ? new Date(b.assignedDate).getTime() : new Date(b.dueDate).getTime();
        return dateB - dateA;
      });
      setHomework(mappedData);
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  }, [selectedStudent]);

  useEffect(() => {
    loadHomework();
  }, [loadHomework]);

  // The page keeps its header while it loads, with the standard loader inside it.
  if (isLoading) {
    return <WebPageShell title="Homework" maxWidth={1000}><LoadingSpinner message="Loading homework..." /></WebPageShell>;
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return { bg: '#FEF3C7', text: '#D97706' }; // light amber / amber
      case 'submitted': return { bg: '#E0F2FE', text: '#0284C7' }; // light sky / sky
      case 'graded': return { bg: '#DCFCE7', text: '#16A34A' }; // light green / green
      case 'overdue': return { bg: '#FEE2E2', text: '#DC2626' }; // light red / red
      default: return { bg: '#F1F5F9', text: '#64748B' }; // slate
    }
  };

  const formatDueDate = (dateStr: string) => {
    // Basic fallback logic, matching image 'Due Today', 'Due Tomorrow', 'Due Jun 22'
    const due = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = (due.getTime() - today.getTime()) / (1000 * 3600 * 24);
    if (diff === 0) return 'Due Today';
    if (diff === 1) return 'Due Tomorrow';
    return `Due ${due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  };

  const content = (
    <View style={[styles.container, isNested && { backgroundColor: 'transparent' }]}>
      <FlatList
        data={homework}
        scrollEnabled={!isNested}
        keyExtractor={item => item.id}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={() => {
              setRefreshing(true);
              loadHomework();
            }}
          />
        }
        renderItem={({ item }) => {
          const displayStatus = item.status === 'overdue' ? 'pending' : item.status;
          const statusColors = getStatusColor(displayStatus);
          return (
            <TouchableOpacity
              style={styles.card}
              onPress={() => {
                setSelectedHomework(item);
                setIsModalVisible(true);
              }}>
              <View style={styles.cardTop}>
                <View style={styles.subjectBadge}>
                  <Text style={styles.subject}>{item.subject}</Text>
                </View>
                <View style={[styles.statusBadge, { backgroundColor: statusColors.bg }]}>
                  <Text style={[styles.statusText, { color: statusColors.text }]}>
                    {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
                  </Text>
                </View>
              </View>
              <Text style={styles.hwTitle}>{item.title}</Text>

              <View style={styles.cardFooter}>
                <FeatherIcon name="clock" size={14} color={colors.textSecondary} style={{ marginRight: 6 }} />
                <Text style={styles.dueDate}>
                  {formatDueDate(item.dueDate)}
                </Text>
              </View>
            </TouchableOpacity>
          );
        }}
        ListEmptyComponent={
          <View style={styles.empty}>
            <FeatherIcon name="book-open" size={48} color={colors.textMuted} style={{ marginBottom: 12 }} />
            <Text style={styles.emptyText}>No homework assigned</Text>
          </View>
        }
        contentContainerStyle={styles.list}
      />
    </View>
  );

  const closeModal = () => {
    setIsModalVisible(false);
    setSelectedHomework(null);
  };

  const parseLinks = (text: string) => {
    if (!text) return null;

    // Strip HTML tags first
    let cleanText = text.replace(/<[^>]*>/g, '');

    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = cleanText.split(urlRegex);

    return parts.map((part, idx) => {
      if (urlRegex.test(part)) {
        return (
          <Text
            key={idx}
            style={styles.linkText}
            onPress={() => {
              try {
                Linking.openURL(part);
              } catch (error) {
                console.error('Error opening URL:', error);
              }
            }}
          >
            {part}
          </Text>
        );
      }
      return part;
    });
  };

  const homeworkModal = selectedHomework ? (
    <Modal visible={isModalVisible} transparent animationType="fade" onRequestClose={closeModal}>
      <View style={styles.modalOverlay}>
        <View style={[styles.modalContent, isMobile && styles.modalContentMobile]}>
          <ScrollView showsVerticalScrollIndicator={false}>
            {/* Header */}
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>{selectedHomework.title}</Text>
              <TouchableOpacity style={styles.modalCloseBtn} onPress={closeModal}>
                <FeatherIcon name="x" size={20} color={colors.textPrimary} />
              </TouchableOpacity>
            </View>

            {/* Subject & Status */}
            <View style={styles.badgeRow}>
              <View style={styles.subjectBadge}>
                <Text style={styles.subject}>{selectedHomework.subject}</Text>
              </View>
              <View style={[styles.statusBadge, { backgroundColor: getStatusColor(selectedHomework.status === 'overdue' ? 'pending' : selectedHomework.status).bg }]}>
                <Text style={[styles.statusText, { color: getStatusColor(selectedHomework.status === 'overdue' ? 'pending' : selectedHomework.status).text }]}>
                  {(selectedHomework.status === 'overdue' ? 'Pending' : selectedHomework.status).charAt(0).toUpperCase() + (selectedHomework.status === 'overdue' ? 'Pending' : selectedHomework.status).slice(1)}
                </Text>
              </View>
            </View>

            {/* Description */}
            {selectedHomework.description && (
              <View style={styles.descriptionSection}>
                <Text style={styles.sectionLabel}>Description</Text>
                <Text style={styles.descriptionText}>
                  {parseLinks(selectedHomework.description)}
                </Text>
              </View>
            )}

            {/* Dates */}
            <View style={styles.datesSection}>
              <View style={styles.dateItem}>
                <FeatherIcon name="calendar" size={14} color={colors.textSecondary} />
                <View style={styles.dateInfo}>
                  <Text style={styles.dateLabel}>Assigned</Text>
                  <Text style={styles.dateValue}>
                    {selectedHomework.assignedDate
                      ? new Date(selectedHomework.assignedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : '-'}
                  </Text>
                </View>
              </View>
              <View style={styles.dateItem}>
                <FeatherIcon name="clock" size={14} color={colors.textSecondary} />
                <View style={styles.dateInfo}>
                  <Text style={styles.dateLabel}>Due</Text>
                  <Text style={styles.dateValue}>
                    {new Date(selectedHomework.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </Text>
                </View>
              </View>
            </View>

            {/* Attachments */}
            {selectedHomework.attachments && selectedHomework.attachments.length > 0 && (
              <View style={styles.attachmentsSection}>
                <Text style={styles.sectionLabel}>Attachments</Text>
                {selectedHomework.attachments.map((attUrl: string, idx: number) => (
                   <AttachmentPreview key={idx} url={attUrl} hwTitle={selectedHomework.title} />
                ))}
              </View>
            )}

            {/* Hand in: attach files, add a note, submit - and read the grade
                back once the school publishes it. Submitting refreshes the list
                behind so the card's badge flips to Submitted. */}
            <HomeworkHandInSection homeworkId={selectedHomework.id} source={selectedHomework.source} collectsUpload={selectedHomework.collectsUpload} teacherFiles={selectedHomework.teacherFiles} onSubmitted={loadHomework} />

            {/* Close Button */}
            <TouchableOpacity style={styles.closeButton} onPress={closeModal}>
              <Text style={styles.closeButtonText}>Close</Text>
            </TouchableOpacity>
          </ScrollView>
        </View>
      </View>
    </Modal>
  ) : null;

  if (isNested) {
    return (
      <>
        {content}
        {homeworkModal}
      </>
    );
  }

  return (
    <>
      <WebPageShell title="Homework" maxWidth={1000}>{content}</WebPageShell>
      {homeworkModal}
    </>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.background },
  list: { padding: Spacing.md },
  card: {
    backgroundColor: colors.card,
    borderRadius: 16,
    padding: Spacing.lg,
    marginBottom: Spacing.md,
    elevation: 2,
    shadowColor: colors.primary,
    shadowOpacity: 0.05,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
  },
  cardTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: Spacing.sm,
  },
  subjectBadge: {
    backgroundColor: colors.primaryLight, // light blue
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: BorderRadius.full,
  },
  subject: {
    fontSize: FontSize.xs,
    fontWeight: '700',
    color: colors.primary,
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: BorderRadius.full,
  },
  statusText: { fontSize: FontSize.xs, fontWeight: '700' },
  hwTitle: {
    fontSize: FontSize.md,
    fontWeight: '700',
    color: colors.textPrimary,
    marginBottom: 8,
    marginTop: 4,
  },
  cardFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  dueDate: {
    fontSize: FontSize.sm,
    color: colors.textSecondary,
    fontWeight: '500',
  },
  empty: { alignItems: 'center', paddingVertical: 60 },
  emptyText: { fontSize: FontSize.md, color: colors.textSecondary },
  // Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: colors.overlay,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: colors.card,
    borderRadius: 20,
    width: '90%',
    maxWidth: 600,
    maxHeight: '90%',
    paddingHorizontal: 24,
    paddingVertical: 24,
  } as any,
  modalContentMobile: {
    width: '100%',
    maxWidth: '100%',
    height: '100%',
    maxHeight: '100%',
    borderRadius: 0,
    paddingHorizontal: 16,
    paddingVertical: 16,
  } as any,
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '800',
    color: colors.textPrimary,
    flex: 1,
  },
  modalCloseBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: colors.divider,
    justifyContent: 'center',
    alignItems: 'center',
  },
  badgeRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 16,
  } as any,
  descriptionSection: {
    marginBottom: 16,
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: colors.textMuted,
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  descriptionText: {
    fontSize: 14,
    color: colors.textPrimary,
    fontWeight: '500',
    lineHeight: 20,
  },
  linkText: {
    color: colors.primary,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  datesSection: {
    marginBottom: 16,
    gap: 12,
  } as any,
  dateItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  } as any,
  dateInfo: {
    flex: 1,
  },
  dateLabel: {
    fontSize: 12,
    color: colors.textMuted,
    fontWeight: '600',
  },
  dateValue: {
    fontSize: 14,
    color: colors.textPrimary,
    fontWeight: '700',
    marginTop: 2,
  },
  attachmentsSection: {
    marginBottom: 20,
  },
  attachmentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 12,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: colors.border,
  },
  attachmentIconBox: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: colors.primaryLight,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  attachmentItemInfo: {
    flex: 1,
  },
  attachmentName: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.textPrimary,
    marginBottom: 2,
  },
  attachmentHint: {
    fontSize: 12,
    color: colors.primary,
    fontWeight: '500',
  },
  closeButton: {
    backgroundColor: colors.primary,
    borderRadius: 12,
    paddingVertical: 12,
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginTop: 16,
  },
  closeButtonText: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '700',
  },
});
